/**
 * F2 — Pairing code for LAN projection.
 *
 * Format: `AGPJ1-<ROOM_CODE>-<KEY_base64url>` — same scheme as the
 * P2P transfer (LSTP1): the room is the public id of the ephemeral signaling
 * (`/api/p2p/*`, blind relay) and the one-time AES-256 key travels ONLY in the
 * code/QR between presenter and projector. The server can neither read NOR
 * forge the signaling (without the key there is no MITM of the DTLS handshake).
 */

import { TRANSFER_KEY_BYTES } from "../p2p/crypto";
import { base64UrlToBytes, bytesToBase64Url } from "../p2p/encoding";

export const PROJECTION_CODE_PREFIX = "AGPJ1";

export interface ParsedProjectionCode {
  roomCode: string;
  keyBytes: Uint8Array;
}

export function buildProjectionCode(roomCode: string, keyBytes: Uint8Array): string {
  if (keyBytes.length !== TRANSFER_KEY_BYTES) {
    throw new Error("Invalid projection key length");
  }
  if (!/^[A-Z2-9]{4,16}$/.test(roomCode)) {
    throw new Error("Invalid room code");
  }
  return `${PROJECTION_CODE_PREFIX}-${roomCode}-${bytesToBase64Url(keyBytes)}`;
}

/** Returns `null` if the text is not a valid projection code. */
export function parseProjectionCode(raw: string): ParsedProjectionCode | null {
  const trimmed = raw.trim();
  const match = /^AGPJ1-([A-Za-z2-9]{4,16})-([A-Za-z0-9_-]{43})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  let keyBytes: Uint8Array;
  try {
    keyBytes = base64UrlToBytes(match[2]!);
  } catch {
    return null;
  }
  if (keyBytes.length !== TRANSFER_KEY_BYTES) {
    return null;
  }
  return { roomCode: match[1]!.toUpperCase(), keyBytes };
}
